import { useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { formatDate } from '../lib/format';

/** Operator context note on a logical secret (migration 020) — display for all
 *  visible roles, inline edit for root/maintainer (same gate as level). Targets
 *  the CANONICAL name: all visible env-instances carry the note together. */

export const SET_SECRET_NOTES = gql`
  mutation SetSecretNotes($name: String!, $notes: String!) {
    setSecretNotes(name: $name, notes: $notes)
  }
`;

export function SecretNotes({
  name,
  notes,
  notesSetBy,
  notesSetAt,
  canEdit,
  onChanged,
}: {
  name: string;
  notes?: string;
  notesSetBy?: string;
  notesSetAt?: string;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes ?? '');
  const [save, { loading }] = useMutation(SET_SECRET_NOTES, {
    onCompleted: () => {
      setEditing(false);
      onChanged();
    },
  });

  if (!notes && !canEdit) return null;

  if (editing) {
    return (
      <div className="secret-note">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={5}
          maxLength={4000}
          placeholder="Operator context — e.g. migration provenance, dual-write rules, doc links. Empty = clear."
          style={{ width: '100%', fontFamily: 'inherit' }}
          className="text-xs"
        />
        <div className="btn-row" style={{ marginTop: 4 }}>
          <button
            className="btn btn-sm btn-primary"
            disabled={loading}
            onClick={() => void save({ variables: { name, notes: draft } })}
          >
            {loading ? 'Saving…' : 'Save note'}
          </button>
          <button
            className="btn btn-sm btn-ghost"
            disabled={loading}
            onClick={() => {
              setDraft(notes ?? '');
              setEditing(false);
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (!notes) {
    return (
      <div style={{ margin: '6px 0' }}>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={() => setEditing(true)}
          title="add operator context (root/maintainer)"
        >
          + Add note
        </button>
      </div>
    );
  }

  return (
    <div className="secret-note">
      <div className="secret-note-body">{notes}</div>
      <div className="muted secret-note-meta">
        <span>
          note set by {notesSetBy ?? '—'}
          {notesSetAt ? ` on ${formatDate(notesSetAt)}` : ''}
        </span>
        {canEdit && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => {
              setDraft(notes);
              setEditing(true);
            }}
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}
